import { getStore } from '../store/configureStore';
import assets from './assets';
import { isApp } from './environment';

const baseUrl = isApp() ? getStore().getState().config.baseUrl : '';

export const getSrc = imageSize => {
  const { src } = imageSize;
  if (!src) return src;
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  if (src.startsWith('/')) return `${baseUrl}${src}`;
  return src;
};

export const getImageSize = (image, cropType, sizeSelector, options = {}) => {
  const { searchHighToLow, getLargestInstead } = options;

  if (!image?.crops) {
    return null;
  }

  const crop = image.crops.find(c => c.type === cropType);

  if (!crop?.sizes) {
    return null;
  }

  const sizesList = searchHighToLow ? [...crop.sizes].reverse() : [...crop.sizes];
  const foundImageSize = sizesList.find(sizeSelector);

  if (foundImageSize) {
    return { ...foundImageSize, src: getSrc(foundImageSize) };
  }

  if (getLargestInstead) {
    const largestSize = searchHighToLow ? sizesList[0] : sizesList[sizesList.length - 1];
    if (largestSize) {
      return { ...largestSize, src: getSrc(largestSize) };
    }
  }

  return null;
};

export const getImageSrc = (image, cropType, sizeSelector, options) =>
  getImageSize(image, cropType, sizeSelector, options)?.src;

export const getImageSrcSet = (image, cropType, selectionCriteria, options = {}) => {
  const { searchHighToLow } = options;
  let sizeFilter;
  let desiredSizes;
  if (typeof selectionCriteria === 'function') {
    sizeFilter = selectionCriteria;
  } else if (Array.isArray(selectionCriteria)) {
    desiredSizes = selectionCriteria;
  } else {
    throw new Error('Selection criteria must be a filter function or an array of desired sizes');
  }
  if (image?.crops) {
    const crop = image.crops.find(c => c.type === cropType);
    if (crop?.sizes) {
      if (sizeFilter) {
        return crop.sizes
          .filter(sizeFilter)
          .map(option => `${getSrc(option)} ${option.w}w`)
          .join(',');
      } else if (desiredSizes?.length) {
        const sizesList = searchHighToLow ? crop.sizes.slice().reverse() : crop.sizes;
        let cursor = 0;
        return desiredSizes
          .map(desiredSize => {
            const bestMatchIndex = sizesList.findIndex(
              size =>
                (desiredSize.w > 0 && size.w >= desiredSize.w) ||
                (desiredSize.h > 0 && size.h >= desiredSize.h),
            );
            const bestMatch = sizesList[bestMatchIndex] || sizesList[cursor + 1];
            if (bestMatchIndex) {
              cursor = bestMatchIndex;
            } else {
              cursor += 1;
            }
            if (bestMatch) {
              const label = desiredSize.x ? `${desiredSize.x}x` : `${bestMatch.w}w`;
              return `${getSrc(bestMatch)} ${label}`;
            }
            return null;
          })
          .filter(item => !!item)
          .join(',');
      }
    }
  }
  return null;
};

export const getCollectionCoverImage = (
  coverImageFrom,
  customCoverImage,
  uploadedCoverImage,
  options = {},
) => {
  // Priority: uploaded > custom > content > book

  // Handle uploaded cover image (highest priority)
  if (uploadedCoverImage) {
    try {
      const parsed =
        typeof uploadedCoverImage === 'string'
          ? JSON.parse(uploadedCoverImage)
          : uploadedCoverImage;

      if (parsed?.crops?.length > 0) {
        const shareCrop = parsed.crops.find(c => c.type === 'share');
        if (shareCrop?.sizes?.length > 0) {
          const bestSize = shareCrop.sizes.find(s => s.w >= 1200) || shareCrop.sizes[0];
          return {
            source: getSrc(bestSize),
            width: bestSize.w,
            height: bestSize.h,
          };
        }
      }
    } catch (e) {
      console.error('Failed to parse uploaded_cover_image:', e);
    }
  }

  if (customCoverImage) {
    const customUrl = customCoverImage.startsWith('/')
      ? customCoverImage
      : `covers/${customCoverImage}`;
    return {
      source: assets.collectionUrl(customUrl, {
        resize: options.resize || 'fit-in',
        width: options.w || 1000,
        height: options.h || 1000,
        smart: true,
      }),
      width: options.w || 1000,
      height: options.h || 1000,
    };
  }
  if (coverImageFrom) {
    const primaryImage = coverImageFrom.primary_image;

    if (!primaryImage && !coverImageFrom.book.image_name) {
      return {
        source: 'http://static.ckbk.com/images/ckbk-logo.png',
        width: options.w || 240,
        height: options.h || 240,
      };
    } else if (!primaryImage) {
      return {
        source: assets.coverUrl(coverImageFrom.book.image_name, {
          resize: options.resizeBook || 'fit-in',
          width: options.w || 240,
          height: options.h || 240,
          background: options.background,
        }),
        width: options.w || 240,
        height: options.h || 240,
      };
    }
    const contentImage =
      getImageSize(
        primaryImage,
        options.crop || 'share',
        options.sizeSelector || (size => size.w <= 2000 && size.h <= 2000),
      ) ||
      getImageSize(primaryImage, options.crop || 'share', size => size.w <= 2000 && size.h <= 2000);

    if (!contentImage) {
      // Fallback to book cover or default image if no content image is available
      if (coverImageFrom.book && coverImageFrom.book.image_name) {
        return {
          source: assets.coverUrl(coverImageFrom.book.image_name, {
            resize: options.resizeBook || 'fit-in',
            width: options.w || 240,
            height: options.h || 240,
            background: options.background,
          }),
          width: options.w || 240,
          height: options.h || 240,
        };
      }
      return {
        source: 'http://static.ckbk.com/images/ckbk-logo.png',
        width: options.w || 240,
        height: options.h || 240,
      };
    }

    return {
      source: contentImage.src.startsWith('http')
        ? contentImage.src
        : `${baseUrl}${contentImage.src}`,
      width: contentImage.w,
      height: contentImage.h,
    };
  }

  return {
    source: assets.coverUrl('default', {
      resize: 'fit-in',
      width: 240,
      height: 240,
      background: options.background,
    }),
    width: 240,
    height: 240,
  };
};

/**
 * Get the effective cover image for a collection based on priority:
 * uploaded_cover_image -> cover_recipe -> cover_image
 *
 * This function centralizes the logic for determining which image should be used
 * as the cover image for a collection, following the same priority order used
 * throughout the application.
 *
 * @param {Object} params - The parameters object
 * @param {string|Object} params.uploaded_cover_image - JSON string or object containing uploaded cover image data
 * @param {Object} params.cover_recipe - Recipe object with primary_image field
 * @param {Object} params.cover_image - Default cover image object with book and primary_image fields
 * @returns {Object} Effective cover image object with primary_image field set according to priority
 */
export const getEffectiveCoverImage = ({ uploaded_cover_image, cover_recipe, cover_image }) => {
  // Start with the default cover_image
  let effectiveCoverImage = cover_image || {
    book: {
      image_permission: false,
    },
    primary_image: null,
  };

  // Priority 1: uploaded_cover_image (highest priority)
  if (uploaded_cover_image) {
    try {
      const parsed =
        typeof uploaded_cover_image === 'string'
          ? JSON.parse(uploaded_cover_image)
          : uploaded_cover_image;

      if (parsed && parsed.crops && parsed.crops.length > 0) {
        // For display purposes, we use the uploaded image as primary_image
        effectiveCoverImage = {
          ...cover_image,
          primary_image: parsed,
        };
        return effectiveCoverImage;
      }
    } catch (e) {
      console.error('Failed to parse uploaded_cover_image:', e);
    }
  }

  // Priority 2: cover_recipe (if no uploaded image)
  if (cover_recipe?.primary_image) {
    effectiveCoverImage = {
      ...cover_image,
      primary_image: cover_recipe.primary_image,
    };
    return effectiveCoverImage;
  }

  // Priority 3: cover_image as-is (fallback)
  return effectiveCoverImage;
};

export default {
  getImageSrc,
  getImageSize,
  getImageSrcSet,
  getCollectionCoverImage,
  getEffectiveCoverImage,
};
